import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/common/toast-context';
import { useErrorMessage } from '@/features/auth/useAuthErrorMessage';

import { useToggleBlock } from './useDiscovery';

export interface BlockTarget {
  id: string;
  username: string;
  displayName: string;
  isBlocked: boolean;
}

interface BlockDialogProps {
  target: BlockTarget | null;
  onClose: () => void;
}

/**
 * Blocking is symmetric and it silently removes follows in both directions
 * (spec §8.6), so it is worth one confirmation step rather than being a
 * single tap in an overflow menu.
 */
export function BlockDialog({ target, onClose }: BlockDialogProps) {
  const { t } = useTranslation();
  const errorMessage = useErrorMessage();
  const toggle = useToggleBlock();
  const { showToast } = useToast();

  const confirm = (): void => {
    if (!target) return;

    toggle.mutate(
      { userId: target.id, isBlocked: target.isBlocked },
      {
        onSuccess: () => {
          showToast(
            target.isBlocked
              ? t('safety.unblocked', { username: target.username })
              : t('safety.blocked', { username: target.username }),
            'success',
          );
          onClose();
        },
      },
    );
  };

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      aria-labelledby="block-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle id="block-title">
        {target?.isBlocked
          ? t('safety.unblockConfirmTitle', { username: target.username })
          : t('safety.blockConfirmTitle', { username: target?.username ?? '' })}
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={1.5}>
          {toggle.isError && (
            <Alert severity="error" role="alert">
              {errorMessage(toggle.error, t('safety.blockFailed'))}
            </Alert>
          )}

          {target?.isBlocked ? (
            <Typography variant="body2">
              {t('safety.unblockDetail', { name: target.displayName })}
            </Typography>
          ) : (
            <>
              <Typography variant="body2">
                {t('safety.blockDetail', { name: target?.displayName ?? '' })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('safety.blockFollowNote')}
              </Typography>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="text" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          color={target?.isBlocked ? 'primary' : 'bad'}
          onClick={confirm}
          disabled={toggle.isPending}
        >
          {toggle.isPending
            ? t('common.working')
            : target?.isBlocked
              ? t('safety.unblock')
              : t('safety.block')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
